import { TFunction } from 'i18next';
import { MediaContentType } from '../../../../api/MediaContentTypeApi';
import { StudentMaterialCount, TeacherMaterialCount } from '../api/UserMaterialCountApi';
import { CategoricalContent, CategoricalContentPerModule } from '../context/CategoricalContentTypeContext';

const BASE = 'PAGES.CATEGORICAL.VIRTUAL_CATEGORIES';

export const virtualContent = {
  teacher: {
    userSlideSets: 'teacher-user-slide-sets',
    slideSets: 'teacher-slide-sets',
    userMaterial: 'teacher-user-material',
  },
  student: {
    sharedMaterial: 'student-shared-user-material',
    sharedSlideSets: 'student-shared-slide-sets',
    slideSets: 'student-slide-sets',
  },
};

export const getCategoricalContent = (contentTypes: MediaContentType[]) => {
  return contentTypes
    .reduce<CategoricalContent[]>((acc, mediaContent) => {
      const existingCategory = acc.find(x => x.name === mediaContent.category);
      if (existingCategory) {
        const existingContentType = existingCategory.contentTypes.find(ct => ct.id === mediaContent.id);
        if (existingContentType) {
          existingContentType.used += mediaContent.used;
        } else {
          existingCategory.contentTypes.push(mediaContent);
        }
      } else if (mediaContent.category) {
        acc.push({ name: mediaContent.category, contentTypes: [mediaContent] });
      } else {
        acc.push({ name: mediaContent.name, contentTypes: [mediaContent] });
      }
      return acc;
    }, [])
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
};

export const getTeacherVirtualCategories = (
  count: TeacherMaterialCount | StudentMaterialCount,
  t: TFunction<'translation', undefined>,
) => {
  const categories: CategoricalContent[] = [];

  if ('sharedMaterial' in count) {
    return categories;
  }

  if (count.userMaterial > 0) {
    categories.push({
      name: t(`${BASE}.TEACHER.USER_MATERIAL.CATEGORY`),
      contentTypes: [
        {
          id: virtualContent.teacher.userMaterial,
          name: t(`${BASE}.TEACHER.USER_MATERIAL.TYPE`),
          used: count.userMaterial,
        },
      ],
    });
  }

  if (count.userSlideSets > 0 || count.slideSets > 0) {
    categories.push({
      name: t(`${BASE}.TEACHER.SLIDE_SETS.CATEGORY`),
      contentTypes: [
        ...(count.userSlideSets > 0
          ? [
              {
                id: virtualContent.teacher.userSlideSets,
                name: t(`${BASE}.TEACHER.SLIDE_SETS.TYPES.USER_SLIDE_SETS`),
                used: count.userSlideSets,
              },
            ]
          : []),
        ...(count.slideSets > 0
          ? [
              {
                id: virtualContent.teacher.slideSets,
                name: t(`${BASE}.TEACHER.SLIDE_SETS.TYPES.SLIDE_SETS`),
                used: count.slideSets,
              },
            ]
          : []),
      ],
    });
  }

  return categories;
};

export const getStudentVirtualCategories = (
  count: StudentMaterialCount | TeacherMaterialCount,
  t: TFunction<'translation', undefined>,
) => {
  if (!('sharedMaterial' in count)) {
    return [];
  }

  const categories: CategoricalContent[] = [];

  if (count.sharedMaterial > 0 || count.sharedSlideSets > 0) {
    categories.push({
      name: t(`${BASE}.STUDENT.SHARED_MATERIAL.CATEGORY`),
      contentTypes: [
        ...(count.sharedMaterial > 0
          ? [
              {
                id: virtualContent.student.sharedMaterial,
                name: t(`${BASE}.STUDENT.SHARED_MATERIAL.TYPES.SHARED_MATERIAL`),
                used: count.sharedMaterial,
              },
            ]
          : []),
        ...(count.sharedSlideSets > 0
          ? [
              {
                id: virtualContent.student.sharedSlideSets,
                name: t(`${BASE}.STUDENT.SHARED_MATERIAL.TYPES.SHARED_USER_SLIDE_SETS`),
                used: count.sharedSlideSets,
              },
            ]
          : []),
      ],
    });
  }

  if (count.slideSets > 0) {
    categories.push({
      name: t(`${BASE}.STUDENT.SLIDE_SETS.CATEGORY`),
      contentTypes: [
        ...(count.slideSets > 0
          ? [
              {
                id: virtualContent.student.slideSets,
                name: t(`${BASE}.STUDENT.SLIDE_SETS.TYPE`),
                used: count.slideSets,
              },
            ]
          : []),
      ],
    });
  }

  return categories;
};

const combineDuplicateContentTypes = (contentTypes: MediaContentType[]) => {
  return contentTypes.reduce<MediaContentType[]>((acc, contentType) => {
    const existingContentType = acc.find(x => x.id === contentType.id);
    if (existingContentType) {
      existingContentType.used += contentType.used;
    } else {
      acc.push({ ...contentType });
    }
    return acc;
  }, []);
};

export const flattenCategoricalMaterialPerModule = (categoricalContentPerModule: CategoricalContentPerModule) => {
  const flattened = Object.values(categoricalContentPerModule).flat();

  const merged = flattened.reduce<CategoricalContent[]>((acc, categoryContent) => {
    const existingCategory = acc.find(x => x.name === categoryContent.name);

    if (existingCategory) {
      const allContentTypes = categoryContent.contentTypes.concat(existingCategory.contentTypes);
      const contentTypes = combineDuplicateContentTypes(allContentTypes);

      existingCategory.contentTypes = contentTypes;
    } else {
      acc.push({ ...categoryContent });
    }
    return acc;
  }, []);

  return merged.map(category => ({
    ...category,
    contentTypes: [...category.contentTypes].sort((a, b) => a.name.localeCompare(b.name)),
  }));
};
